// Image workspace — the six user-facing semantic shortcut cards
// (Owner 19-10). Cards are SHORTCUTS, not mandatory tabs: the user can
// always just type a prompt (the backend infers the operation).
//
// Capability rule (binding): the backend registry is the authority.
//   available  -> interactive card
//   candidate  -> visible but NOT interactive, «Скоро» badge — never
//                 advertised as a working production capability
//   disabled   -> hidden
// The public /capability endpoint only lists AVAILABLE operations, so
// any card whose operation is absent from the list renders as
// candidate («Скоро») — the UI never fabricates capability.
import {
  Wand2,
  Pencil,
  Mountain,
  Shuffle,
  Images,
  LayoutGrid,
  type LucideIcon,
} from "lucide-react";
import type { ImageGenCapability } from "@/lib/imagegen/api";

export interface ImageModeCard {
  id: string;
  // Backend semantic operation for the card. The «По образцу» card maps
  // to single_reference (1 image) / multi_reference (2+ images) — the
  // backend resolves by attachment count; the card stays ONE card.
  operation: string;
  title: string;
  description: string;
  icon: LucideIcon;
}

export const IMAGE_MODE_CARDS: ImageModeCard[] = [
  {
    id: "generation",
    operation: "text_to_image",
    title: "Генерация",
    description: "Создать изображение по описанию",
    icon: Wand2,
  },
  {
    id: "edit",
    operation: "image_edit",
    title: "Редактирование",
    description: "Изменить изображение инструкцией",
    icon: Pencil,
  },
  {
    id: "scene",
    operation: "scene_change",
    title: "Изменение фона",
    description: "Сохранить объект, заменить фон",
    icon: Mountain,
  },
  {
    id: "object",
    operation: "object_replacement",
    title: "Замена объекта",
    description: "Заменить или убрать объект",
    icon: Shuffle,
  },
  {
    id: "reference",
    operation: "single_reference",
    title: "По образцу",
    description: "Один или несколько референсов",
    icon: Images,
  },
  {
    id: "layout",
    operation: "layout_bbox",
    title: "Композиция",
    description: "Управление расположением объектов",
    icon: LayoutGrid,
  },
];

export type CardState = "available" | "candidate";

/** Card interactivity from the server capability registry. */
export function cardState(
  card: ImageModeCard,
  capability: ImageGenCapability | null
): CardState {
  // Pre-V2 backends omit `operations` entirely — the production truth
  // there is exactly one working operation (text_to_image on FLUX.2).
  // V2 backends send the available-only list; we obey it strictly.
  const list = capability
    ? capability.operations ?? [{ code: "text_to_image" }]
    : [];
  const ops = new Set(list.map((o) => o.code));
  // «По образцу» is available when either reference operation is.
  if (card.id === "reference") {
    return ops.has("single_reference") || ops.has("multi_reference")
      ? "available"
      : "candidate";
  }
  return ops.has(card.operation) ? "available" : "candidate";
}

/** Operation to send for a selected card (explicit user choice).
 *  For «По образцу» the count-resolved operation is inferred by the
 *  backend from attachments — the client sends nothing extra here. */
export function cardOperation(card: ImageModeCard): string | undefined {
  if (card.id === "reference") return undefined; // backend resolves by count
  return card.operation;
}
